import React, { useContext, useMemo } from 'react';
import type { RollingChartProps, RollingPeriodEnum } from './types';
import { getAnalysisLabels } from 'venn-utils';
import AnalysisLineChart from './internal/AnalysisLineChart';
import { AnalysisViewContext } from '../../contexts';
import { PERIODS } from './internal/AnalysisChartLegend';
import useCustomRollingWindow from './internal/useCustomRollingWindow';
import { isNil } from 'lodash';

const AnalysisRollingBetaChart = ({
  frequency,
  analysisGroup,
  subject,
  actualTimeFrame,
  relative,
  noChartErrorComponent,
  noChartTrackingProps,
  downloadMetaData,
  categoryActive,
}: RollingChartProps) => {
  const fullPeriodLabels = useMemo(() => {
    const titleType = getAnalysisLabels(subject.type).main;
    return {
      absolute: `${titleType}'s Full-History Beta`,
      relative: `${titleType}'s Full-History Beta`,
    };
  }, [subject.type]);

  const { rollingBetaPeriod, onUpdateAnalysisViewParam } = useContext(AnalysisViewContext);
  const { chartDataWithRollingPeriods, customPeriodLoading, setOptimisticLoading } = useCustomRollingWindow(
    subject,
    analysisGroup,
    rollingBetaPeriod,
    relative,
    categoryActive,
    actualTimeFrame,
    'ROLLING_BETA',
    'rollingBeta',
    fullPeriodLabels,
    noChartTrackingProps?.trackingId,
  );

  const header = 'Rolling Beta to Benchmark';
  return (
    <AnalysisLineChart
      defaultRollingPeriod={PERIODS.find(({ key }) => key === rollingBetaPeriod)}
      rollingPeriodSettings={rollingBetaPeriod}
      loadingSerieData={customPeriodLoading}
      onRollingPeriodChange={(updatedPeriodKey?: RollingPeriodEnum, customPeriodMonths?: number) => {
        if (!isNil(updatedPeriodKey)) {
          onUpdateAnalysisViewParam({ rollingBetaPeriod: { period: updatedPeriodKey } });
          return;
        }
        if (!isNil(customPeriodMonths)) {
          setOptimisticLoading(true);
          onUpdateAnalysisViewParam({ rollingBetaPeriod: { customMonths: customPeriodMonths } });
        }
      }}
      allowCustomWindow
      analysisSubject={subject}
      frequency={frequency}
      relative={relative}
      chartDataWithRollingPeriods={chartDataWithRollingPeriods}
      start={actualTimeFrame.startTime}
      end={actualTimeFrame.endTime}
      header={header}
      noChartErrorComponent={noChartErrorComponent}
      noChartTrackingProps={noChartTrackingProps}
      yAxisUnitFormat="ratio"
      exportPNGEnabled
      exportXLSXEnabled
      dataExportEvent={{
        description: 'ROLLING_BETA',
        relativeToBenchmark: relative,
        subjectType: subject.type,
        subjectId: subject.id,
        userUploaded: subject.userUploaded,
      }}
      downloadMetaData={downloadMetaData}
    />
  );
};

export default AnalysisRollingBetaChart;
